"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Stethoscope, Calendar, Mountain, Users, Cpu, Sparkles, ArrowRight, Trophy } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const REASONS = [
  { icon: Stethoscope, title: "The Medical Method", body: "The only operator at this level whose discipline was forged in clinical practice. Clinical rigor is the operating system, not a biographical detail.", accent: "red", gradient: "from-red/15" },
  { icon: Calendar, title: "A Decade of Building", body: "Not a consulting career. A single organization, co-built and sustained for ten years, scaled across eighteen countries, through every global disruption.", accent: "purple", gradient: "from-purple/15" },
  { icon: Mountain, title: "Sovereign-Scale Experience", body: "Operations at the level where stakes are national, clients are sovereign, and failure is measured in stability. Verified, not theoretical.", accent: "cyan", gradient: "from-cyan/15" },
  { icon: Users, title: "The 70% Operating Model", body: "A decade of evidence that the world's most overlooked talent pool — women — produces the most reliable operational outcomes. Not a values statement. A talent strategy with results.", accent: "amber-gold", gradient: "from-amber-gold/15" },
  { icon: Sparkles, title: "Cross-Domain Mastery", body: "Medicine, technology, and politics in one operator. The intersection is where complex, multi-stakeholder, high-stakes problems are solved — and where single-domain specialists reach their ceiling.", accent: "lime", gradient: "from-lime/15" },
  { icon: Cpu, title: "The Integrated Operating Method", body: "Sixty-plus integrated services, built in-house, operating as a unified system. The integration is the moat. The orchestration is the advantage. Zero third-party dependencies.", accent: "denim", gradient: "from-denim/15" },
];

const TEXT: Record<string, string> = {
  red: "text-red", purple: "text-purple", cyan: "text-cyan", "amber-gold": "text-amber-gold", lime: "text-lime", denim: "text-denim",
};

export function WhyChooseSection() {
  return (
    <Section id="why-choose" ariaLabel="Why Choose Dr. Jyoti Kush" background="muted">
      <SectionHeader
        icon={Trophy}
        overline="Why Choose"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            The Decision-Maker&apos;s <span className="text-gradient-premium">Case.</span>
          </>
        }
        subtitle="Organizations that choose this operator recognize that the stakes they face cannot be addressed by conventional approaches."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {REASONS.map((r, i) => (
          <motion.div
            key={r.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className={`group relative glass-card rounded-2xl p-5 overflow-hidden bg-gradient-to-br ${r.gradient} to-transparent border border-border/30`}
          >
            <div aria-hidden className="absolute -top-12 -right-12 size-40 rounded-full bg-current opacity-[0.04] blur-2xl group-hover:scale-150 transition-transform duration-700" />
            <div className="relative">
              <div className="size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-md mb-3 group-hover:scale-110 group-hover:rotate-3 transition-all">
                <r.icon className="size-5 text-white" />
              </div>
              <h3 className={`text-heading-md mb-2 ${TEXT[r.accent]}`}>{r.title}</h3>
              <p className="text-body-sm text-muted-foreground leading-relaxed">{r.body}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 glass-strong rounded-2xl p-6 md:p-8 gradient-border text-center">
        <h3 className="text-heading-md mb-3">Begin the conversation.</h3>
        <p className="text-body-md text-muted-foreground mb-5 max-w-2xl mx-auto">
          When the outcome must be operational excellence — not a strategy deck, but actual, sustained, verified execution — there is one operator who has built for exactly this.
        </p>
        <Link href="/contact/" className="inline-flex items-center gap-2 text-body-md font-semibold text-red hover:gap-3 transition-all group">
          Start engagement <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </Section>
  );
}
